'use client';

import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

// Backend integration point: replace with API call to fetch member workload data
const members = [
  { id: 'member-001', name: 'Priya Nair', role: 'Curriculum Designer', initials: 'PN', color: 'bg-violet-100 text-violet-700', tasks: 9, capacity: 10, overdue: 2, inProgress: 4 },
  { id: 'member-002', name: 'Kwame Asante', role: 'Instructional Designer', initials: 'KA', color: 'bg-blue-100 text-blue-700', tasks: 5, capacity: 10, overdue: 0, inProgress: 3 },
  { id: 'member-003', name: 'Sofía Delgado', role: 'Content Reviewer', initials: 'SD', color: 'bg-emerald-100 text-emerald-700', tasks: 8, capacity: 10, overdue: 1, inProgress: 5 },
  { id: 'member-004', name: 'Tariq Hassan', role: 'LMS Coordinator', initials: 'TH', color: 'bg-amber-100 text-amber-700', tasks: 10, capacity: 10, overdue: 3, inProgress: 2 },
  { id: 'member-005', name: 'Amara Osei', role: 'Curriculum Designer', initials: 'AO', color: 'bg-pink-100 text-pink-700', tasks: 3, capacity: 10, overdue: 0, inProgress: 2 },
  { id: 'member-006', name: 'Lena Voigt', role: 'Assessment Specialist', initials: 'LV', color: 'bg-cyan-100 text-cyan-700', tasks: 7, capacity: 10, overdue: 1, inProgress: 3 },
  { id: 'member-007', name: 'Raj Mehta', role: 'Content Writer', initials: 'RM', color: 'bg-indigo-100 text-indigo-700', tasks: 6, capacity: 10, overdue: 0, inProgress: 4 },
  { id: 'member-008', name: 'Chioma Eze', role: 'Instructional Designer', initials: 'CE', color: 'bg-rose-100 text-rose-700', tasks: 4, capacity: 10, overdue: 0, inProgress: 2 },
];

function workloadColor(tasks: number, capacity: number) {
  const pct = tasks / capacity;
  if (pct >= 0.9) return 'bg-red-500';
  if (pct >= 0.7) return 'bg-amber-400';
  if (pct >= 0.4) return 'bg-primary';
  return 'bg-green-500';
}

export default function MemberWorkloadPanel() {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div className="bg-card border border-border rounded-xl shadow-card p-5 h-full card-hover">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Team Workload</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Tasks assigned per member</p>
        </div>
        <button className="text-xs text-primary font-medium hover:underline flex items-center gap-0.5 btn-press">
          View all
          <ChevronRight size={13} />
        </button>
      </div>

      <ul className="space-y-2.5">
        {members.map((member) => {
          const pct = Math.round((member.tasks / member.capacity) * 100);
          const isOverloaded = member.tasks >= member.capacity;
          const isHovered = hovered === member.id;

          return (
            <li
              key={member.id}
              onMouseEnter={() => setHovered(member.id)}
              onMouseLeave={() => setHovered(null)}
              className={`relative rounded-lg px-3 py-2.5 transition-colors duration-150 cursor-default ${
                isHovered ? 'bg-muted/60' : ''
              }`}
            >
              <div className="flex items-center gap-2.5 mb-1.5">
                <div
                  className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${member.color}`}
                >
                  {member.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate leading-tight">
                    {member.name}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{member.role}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className={`text-xs font-bold font-tabular ${isOverloaded ? 'text-red-600' : 'text-foreground'}`}>
                    {member.tasks}
                  </span>
                  <span className="text-[10px] text-muted-foreground">/ {member.capacity}</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${workloadColor(member.tasks, member.capacity)}`}
                  style={{ width: `${pct}%` }}
                />
              </div>

              {/* Hover tooltip detail */}
              {isHovered && (
                <div className="absolute right-2 top-2 z-10 flex items-center gap-2 fade-in">
                  {member.overdue > 0 && (
                    <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                      {member.overdue} overdue
                    </span>
                  )}
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {member.inProgress} active
                  </span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}